import {Action} from "@dp-instant/core";
import {inject, injectable} from "inversify";
import {Graphics, RenderTexture, Sprite, Texture} from "pixi.js-legacy";
import {Types2D} from "@dp-instant/interfaces";
import {RootView} from "@dp-instant/2d";
import {LevelData} from "@game/coregame/interfaces/LevelData";
import {TypesGame} from "@game/TypesGame";
import {GenerateMapShapeAction} from "@game/coregame/actions/GenerateMapShapeAction";
import {PlayerColor} from "@game/coregame/settings";
import {color} from "@game/coregame/helpers";
import {PlayerType} from "@game/coregame/interfaces/PlayerType";
import {LevelDataState} from "@game/coregame/interfaces/LevelDataState";
import {SpritesPool} from "@game/coregame/utils/SpritesPool";
import {CookieModel} from "@game/coregame/model/CookieModel";
import {SkinManager} from "../managers/SkinManager";
import {log} from "@dp-instant/utils";

type GenerateMapSpritePayload = {
    activeStage: number;
    data: LevelData;
    area?: {width: number; height: number};
};

@injectable()
export class GenerateMapSpriteAction extends Action<GenerateMapSpritePayload, Sprite> {
    protected data: GenerateMapSpritePayload;

    @inject(TypesGame.cookieModel)
    private cookies: CookieModel;

    @inject(TypesGame.skinManager)
    private skinManager: SkinManager;

    @inject(Types2D.rootView)
    protected rootView: RootView;

    @inject(TypesGame.spritesPool)
    protected spritesPool: SpritesPool;

    @inject(TypesGame.actions.createMapPart)
    protected createMapPart: GenerateMapShapeAction;

    async execute(payload: GenerateMapSpritePayload): Promise<Sprite> {
        const promises: Promise<Sprite>[] = [];
        payload.data.stages.forEach((stage, index) => {
            stage.states.map(async state => {
                const id = `sprite-${payload.data.id}-${state.id}`;
                const p_color = this.getPlayerColor(index);
                promises.push(this.createStateSprite(id, state, p_color));
            });
        });

        const sprites = await Promise.all(promises);
        return this.mergeSprites(sprites, payload.area);
    }

protected fit(cont: Sprite, area: {width: number; height: number}): void {
        const mapScale = this.aspectFactor(cont.width, cont.height, area.width, area.height);
        cont.width *= mapScale;
        cont.height *= mapScale;
    }
    protected aspectFactor(
        innerWidth: number,
        innerHeight: number,
        outerWidth: number,
        outerHeight: number
    ): number {
        const inner = innerWidth / innerHeight;
        const outer = outerWidth / outerHeight;
        return inner >= outer ? outerWidth / innerWidth : outerHeight / innerHeight;
    }
    protected mergeSprites(sprites: Sprite[], area?: {width: number; height: number}): Sprite {
        const cont = new Sprite();
        cont.addChild(...sprites);
        if (area) {
            this.fit(cont, area);
            log.debug("mergeSprites", area.width, area.height);
        } else {
            area = cont.getLocalBounds();
        }
        const options = {width: area.width, height: area.height};
        const renderTexture = RenderTexture.create(options);
        this.rootView.app.renderer.render(cont, renderTexture);
        const sprite = new Sprite(renderTexture);
        Texture.removeFromCache(renderTexture);
        return sprite;
    }

protected async createStateSprite(id: string, data: LevelDataState, playerColor: PlayerColor) {
        const state = await this.createMapPart.run({
            id,
            shapes: data.shapes
        });
        const cont = new Sprite();
        const R = data.radius || 20;
        const dot = this.spritesPool.fromDisplayObject(`base${R}-static`, () =>
            new Graphics().beginFill(0xffffff).drawCircle(0, 0, R).endFill()
        );
        dot.anchor.set(0.5);
        dot.position.set(data.x, data.y);
        const [fill, unit, base] = playerColor;
        state.tint = color.fromHex(fill);
        dot.tint = color.fromHex(base || unit);
        cont.addChild(state);
        cont.addChild(dot);
        return cont;
    }
    private getPlayerColor(stageIndex = 0): PlayerColor {
        // TODO: add more colors
        if (stageIndex <= this.data?.activeStage) {
            return this.skinManager.getColorBy(PlayerType.First);
        }
        return this.skinManager.getColorBy(PlayerType.Neutral);
    }
}
